import { useCallback } from 'react';
import { toast } from '@/components/ui';
import Logger from '@/utils/logger';
import { formatToTitleCase } from '@/utils/formatters';
import { focusField } from '@/utils/focus';
import { type ClientFormData, type CustomizedSections, getDefaultsToRestore, buildSavePayload } from '../utilities/formDataUtils';
import type { UserConfigurationState } from '@/types/userConfiguration';
import { handleStepBack } from '../utilities/stepUtils';
import type { ValidationContextValue } from '@/components/validation';

interface UseClientFormHandlersProps {
  formData: ClientFormData;
  setFormData: (data: ClientFormData | ((prev: ClientFormData) => ClientFormData)) => void;
  customizedSections: CustomizedSections;
  isTravelManuallyEnabled: boolean;
  setIsTravelManuallyEnabled: (enabled: boolean) => void;
  allClients: { id?: string; name?: string | null }[];
  isEditing: boolean;
  currentStep: number;
  setCurrentStep: (step: number | ((prev: number) => number)) => void;
  setCompletedSteps: (steps: boolean[] | ((prev: boolean[]) => boolean[])) => void;
  steps: string[];
  isRolesStepValid: boolean;
  userDefaults: UserConfigurationState | null;
  onNameChange?: (name: string) => void;
  onSave: (updatedData: Partial<ClientFormData>) => Promise<void>;
  ctx: ValidationContextValue;
}

export const useClientFormHandlers = ({
  formData,
  setFormData,
  customizedSections,
  isTravelManuallyEnabled,
  setIsTravelManuallyEnabled,
  isEditing,
  currentStep,
  setCurrentStep,
  setCompletedSteps,
  steps,
  isRolesStepValid,
  userDefaults,
  onNameChange,
  onSave,
  ctx,
}: UseClientFormHandlersProps) => {

  const handleChange = useCallback((field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => {
    // Toast logic can stay here as it mostly depends on the field name and new value.
    const clientNamePart = formData.name?.trim() ? ` for ${formData.name.trim()}` : '';
    if (field === 'short_turn_surcharge_type' && formData.uses_short_turn) {
      let modeLabel = '';
      switch (value) {
        case 'fixed_dollar': modeLabel = 'Fixed Premium'; break;
        case 'offset_overtime': modeLabel = 'Offset Overtime'; break;
        case 'carry_over': modeLabel = 'Carry Over'; break;
        case 'bump_up': modeLabel = 'Bump Up'; break;
        default: modeLabel = String(value);
      }
      toast.success(`Short turn surcharge method set to ${modeLabel}${clientNamePart}`);
    } else if (field === 'short_turn_floor_tier_code' && formData.uses_short_turn && formData.short_turn_surcharge_type === 'offset_overtime') {
      const multiplier = (formData.rate_multipliers ?? []).find(m => m.code === value);
      if (multiplier) toast.success(`Short turn multiplier set to "${multiplier.name}"${clientNamePart}`);
    } else if (field === 'holiday_premium_type' && formData.apply_holiday_premium) {
      let modeLabel = '';
      switch (value) {
        case 'fixed_dollar': modeLabel = 'Fixed Premium Amount'; break;
        case 'offset_overtime': modeLabel = 'Offset Overtime'; break;
        case 'guaranteed_rate': modeLabel = 'Guaranteed Rate'; break;
        case 'hourly_premium': modeLabel = 'Hourly Premium'; break;
        default: modeLabel = String(value);
      }
      toast.success(`Holiday premium type set to ${modeLabel}${clientNamePart}`);
    } else if ((field === 'catalog_holiday_ids' || field === 'custom_holiday_ids') && formData.apply_holiday_premium) {
      const newIds = Array.isArray(value) ? (value as string[]) : [];
      const oldIds = field === 'catalog_holiday_ids'
        ? (formData.catalog_holiday_ids ?? [])
        : (formData.custom_holiday_ids ?? []);
      const added = newIds.length - oldIds.length;
      if (added > 0) {
        toast.success(`${added} holiday${added !== 1 ? 's' : ''} added${clientNamePart}`);
      } else if (added < 0) {
        toast.success(`${Math.abs(added)} holiday${Math.abs(added) !== 1 ? 's' : ''} removed${clientNamePart}`);
      }
    }

    setFormData(prevFormData => {
      let updatedValue = value;
      if (field === 'name' && typeof value === 'string') {
        updatedValue = formatToTitleCase(value);
      }

      const newFormData = { ...prevFormData, [field]: updatedValue };

      // FIX: Logic for meal break Premium Type (auto-set when value entered)
      if (field === 'meal_premium_value' && updatedValue !== null && updatedValue !== '' && !prevFormData.meal_premium_type) {
        newFormData.meal_premium_type = 'fixed';
      }

      // Name-specific side effects
      if (field === 'name') {
        onNameChange?.(updatedValue as string);
      }

      return newFormData;
    });
  }, [formData.name, formData.uses_short_turn, formData.apply_holiday_premium, formData.holiday_premium_type, formData.short_turn_surcharge_type, formData.rate_multipliers, formData.catalog_holiday_ids, formData.custom_holiday_ids, setFormData, onNameChange]);

  const handleFieldBlurWrapper = useCallback((field: keyof ClientFormData) => {
    ctx.markFieldBlurred(field as string);
  }, [ctx]);

  const handleClearFieldValidationWrapper = useCallback((_field: keyof ClientFormData) => {
    // With ValidationProvider, clearing a field's validation is a no-op.
    // The provider re-validates reactively and getFieldError gates on blur state.
    // The field stays "blurred" but if the error is fixed, getFieldError returns null.
  }, []);

  const handleSelectChange = useCallback((field: keyof ClientFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  const handleSwitchChange = useCallback((field: keyof ClientFormData, checked: boolean) => {
    const clientNamePart = formData.name?.trim() ? ` for ${formData.name.trim()}` : '';

    // Handle toasts for all relevant surcharge toggles
    switch (field) {
      case 'apply_holiday_premium':
        toast.success(checked ? `Holiday premium enabled${clientNamePart}` : `Holiday premium disabled${clientNamePart}`);
        break;
      case 'apply_meal_surcharge':
        toast.success(checked ? `Meal break surcharge enabled${clientNamePart}` : `Meal break surcharge disabled${clientNamePart}`);
        break;
      case 'uses_short_turn':
        toast.success(checked ? `Short-turn surcharge enabled${clientNamePart}` : `Short-turn surcharge disabled${clientNamePart}`);
        break;
      case 'apply_overnight_premium':
        toast.success(checked ? `Overnight premium enabled${clientNamePart}` : `Overnight premium disabled${clientNamePart}`);
        break;
    }

    // Handle state updates
    if (field === 'uses_short_turn') {
      setFormData(prev => {
        const newFormData = { ...prev, [field]: checked };

        if (checked) {
          if (!newFormData.short_turn_surcharge_type && !userDefaults?.shortTurnConfig?.surchargeType) {
            const hasExtra = (prev.rate_multipliers ?? []).some(m => m.code !== "STRAIGHT_TIME");
            const defaultMode = hasExtra ? "carry_over" : "fixed_dollar";
            newFormData.short_turn_surcharge_type = defaultMode;
          }
          if (newFormData.short_turn_surcharge_type !== 'fixed_dollar') {
            newFormData.short_turn_fixed_surcharge_amount = null;
          }
          if (newFormData.short_turn_surcharge_type !== 'offset_overtime') {
            newFormData.short_turn_floor_tier_code = null;
          }
        }
        return newFormData;
      });
    } else {
      setFormData(prev => ({ ...prev, [field]: checked }));
    }
  }, [formData.name, userDefaults, setFormData]);

  const handleRevertSection = useCallback((section: 'dayRate' | 'travel' | 'breaks' | 'invoice' | 'roles' | 'multipliers' | 'shortTurn' | 'holiday' | 'mealBreak' | 'overnight' | 'expenses' | 'gear') => {
    if (!userDefaults) return;

    if (section === 'travel') {
      setIsTravelManuallyEnabled(false);
    }
    const defaultsToRestore = getDefaultsToRestore(section, userDefaults);
    setFormData(prev => ({ ...prev, ...defaultsToRestore }));
  }, [userDefaults, setIsTravelManuallyEnabled, setFormData]);

  const handleNext = useCallback(() => {
    const stepScope = String(currentStep);

    // Check for open sub-forms (roles/multipliers)
    if (currentStep === 1 && !isRolesStepValid) {
      toast.error("Please save or cancel the open role form to continue.");
      return;
    }

    // Special structural validation for roles step
    if (currentStep === 1 && (!formData.roles || formData.roles.length === 0)) {
      toast.error("Client must have at least one role.");
      return;
    }

    // Run schema validation for the current step
    const stepErrors = ctx.validate(stepScope);
    if (stepErrors.length > 0) {
      ctx.triggerShake();
      setTimeout(() => focusField(stepErrors[0].field), 100);
      return;
    }

    // Advance
    setCompletedSteps(prev => {
      const newCompleted = [...prev];
      newCompleted[currentStep] = true;
      return newCompleted;
    });
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  }, [currentStep, isRolesStepValid, formData.roles, ctx, setCompletedSteps, setCurrentStep, steps.length]);

  const handleBackWrapper = useCallback(() => {
    handleStepBack(setCurrentStep);
  }, [setCurrentStep]);

  const handleSave = useCallback(async () => {
    // Validate all steps
    const allErrors = ctx.validateAll();

    if (allErrors.length > 0) {
      ctx.triggerShake();
      const firstError = allErrors[0];
      // Find which step the error belongs to
      const errorStep = firstError.scope != null ? Number(firstError.scope) : 0;
      toast.error(`Please fix errors before saving. First up: ${firstError.message}`);
      setCurrentStep(errorStep);
      setTimeout(() => focusField(firstError.field), 100);
      return;
    }

    // Structural requirements (roles exist, STRAIGHT_TIME multiplier exists)
    if (!formData.roles || formData.roles.length === 0) {
      toast.error("Client must have at least one role.");
      setCurrentStep(1);
      return;
    }
    if (!formData.rate_multipliers || !formData.rate_multipliers.some(m => m.code === 'STRAIGHT_TIME')) {
      toast.error("Client must have at least one 'Straight Time' rate multiplier.");
      setCurrentStep(2);
      return;
    }

    const payload = buildSavePayload(formData, customizedSections, isEditing);

    try {
      await onSave(payload);
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error("An unexpected error occurred while saving the client.");
      Logger.error("Error during save operation in ClientForm", err, { component: 'ClientForm' });
      toast.error(err.message);
    }
  }, [formData, customizedSections, isEditing, onSave, setCurrentStep, ctx]);

  return {
    handleChange,
    handleFieldBlurWrapper,
    handleClearFieldValidationWrapper,
    handleSelectChange,
    handleSwitchChange,
    handleRevertSection,
    handleNext,
    handleBackWrapper,
    handleSave,
  };
};
